"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
export default function OurCorporateIdentity() {
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="265022" id="our-corporate-identity" className="bg-[#0B1220] text-white py-16 md:py-24 overflow-hidden relative">
      {}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-[#142033] -skew-x-12 translate-x-1/2 pointer-events-none opacity-50" />
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          
          {}
          <div ref={imageRef} className={`lg:col-span-6 relative transition-all duration-1000 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="relative aspect-auto min-h-[400px] md:min-h-[600px] rounded-lg overflow-hidden border border-white/10 shadow-2xl bg-cover bg-no-repeat bg-center !bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683494/img/corporate_cybersecurity_identity.png)]">
             <img src="https://fetc.ae/wp-content/uploads/2026/05/corporate_cybersecurity_identity.webp" alt="Abu Dhabi skyline representing FETC corporate identity" loading="eager" decoding="async" className="absolute inset-0 h-full w-full object-cover object-center" /> 
              
              
            </div>
            {}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#0F62FE] rounded-lg -z-10 hidden md:block opacity-20 blur-2xl"></div>
          </div>

          {}
          <div ref={contentRef} className={`lg:col-span-6 flex flex-col justify-center space-y-8 transition-all duration-1000 delay-300 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-3">
                <div className="h-px w-8 bg-[#0F62FE]"></div>
                <span className="text-[#0F62FE] font-mono text-lg tracking-widest uppercase relative inline-block overflow-hidden group">
                  ABOUT FETC
                  <span className="absolute inset-0 -translate-x-full group-hover:animate-[shine_1.5s_ease-in-out] bg-linear-to-r from-transparent via-white/30 to-transparent"></span>
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold leading-[1.1] tracking-tighter">
                Cybersecurity, Technology Consulting and System Integration for the UAE
              </h2>
              <div className="space-y-4">
                <p className="text-lg md:text-xl text-white/70 leading-relaxed font-sans max-w-xl">
                  FETC supports government and enterprise organizations with secure technology architecture, cybersecurity governance, infrastructure modernization and practical system integration capabilities.
                </p>
                <div className="flex items-center space-x-2 text-[#0F62FE] font-medium">
                  <MapPin className="size-5" />
                  <span className="text-white/80">Based in Abu Dhabi, United Arab Emirates</span>
                </div>
              </div>
            </div>

            <div className="pt-4 flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-[#0F62FE] hover:bg-[#0043CE] text-white rounded-md px-8 py-7 text-lg font-semibold transition-all duration-300 border-none shadow-lg shadow-[#0F62FE]/20">
                <Link to="/OurCapabilitiesPage">
                  Explore Our Capabilities
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}